import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { useApp } from "../context/AppContext";
import { MovementsAPI } from "../lib/api";
import {
  Plus,
  Printer,
  Trash2,
  ArrowDownToLine,
  ArrowUpFromLine,
  RefreshCw,
  Filter,
} from "lucide-react";
import MovementForm from "../components/MovementForm";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { generateMovimentiPDF } from "../lib/pdf";

export default function Movements() {
  const { products, refresh } = useApp();
  const [openForm, setOpenForm] = useState(false);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    product_id: "all",
    type: "all",
    date_from: "",
    date_to: "",
  });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (f.product_id && f.product_id !== "all") params.product_id = f.product_id;
      if (f.type && f.type !== "all") params.type = f.type;
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      const data = await MovementsAPI.list(params);
      setMovements(data);
    } catch {
      toast.error("Errore nel caricamento dei movimenti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = () => load(filters);
  const reset = () => {
    const f = { product_id: "all", type: "all", date_from: "", date_to: "" };
    setFilters(f);
    load(f);
  };

  const confirmDelete = async () => {
    try {
      await MovementsAPI.remove(deleteTarget.id);
      toast.success("Movimento annullato e giacenza aggiornata");
      await refresh();
      await load();
    } catch {
      toast.error("Errore durante l'annullamento");
    } finally {
      setDeleteTarget(null);
    }
  };

  const filterLabel = useMemo(() => {
    const parts = [];
    if (filters.product_id && filters.product_id !== "all") {
      const p = products.find((x) => x.id === filters.product_id);
      if (p) parts.push(`Prodotto: ${p.name}`);
    }
    if (filters.type && filters.type !== "all")
      parts.push(`Tipo: ${filters.type}`);
    if (filters.date_from) parts.push(`Da: ${filters.date_from}`);
    if (filters.date_to) parts.push(`A: ${filters.date_to}`);
    return parts.join(" · ");
  }, [filters, products]);

  return (
    <Layout
      title="Movimenti"
      subtitle="Storico entrate e uscite del magazzino"
    >
      <div className="panel mb-4">
        <div className="panel-header">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="eyebrow">Filtri</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() =>
                generateMovimentiPDF(
                  movements,
                  filterLabel || `Tutti i movimenti`,
                )
              }
              disabled={movements.length === 0}
              data-testid="print-movements-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Stampa PDF
            </Button>
            <Button
              onClick={() => setOpenForm(true)}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="add-movement-btn"
              disabled={products.length === 0}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo movimento
            </Button>
          </div>
        </div>

        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="space-y-1.5 lg:col-span-2">
            <Label htmlFor="f-product">Prodotto</Label>
            <Select
              value={filters.product_id}
              onValueChange={(v) =>
                setFilters((f) => ({ ...f, product_id: v }))
              }
            >
              <SelectTrigger id="f-product" data-testid="filter-product">
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i prodotti</SelectItem>
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.code} · {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="f-type">Tipo</Label>
            <Select
              value={filters.type}
              onValueChange={(v) => setFilters((f) => ({ ...f, type: v }))}
            >
              <SelectTrigger id="f-type" data-testid="filter-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Entrate + Uscite</SelectItem>
                <SelectItem value="entrata">Solo Entrate</SelectItem>
                <SelectItem value="uscita">Solo Uscite</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="f-from">Dal</Label>
            <Input
              id="f-from"
              type="date"
              value={filters.date_from}
              onChange={(e) =>
                setFilters((f) => ({ ...f, date_from: e.target.value }))
              }
              data-testid="filter-date-from"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="f-to">Al</Label>
            <Input
              id="f-to"
              type="date"
              value={filters.date_to}
              onChange={(e) =>
                setFilters((f) => ({ ...f, date_to: e.target.value }))
              }
              data-testid="filter-date-to"
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-5 flex items-center gap-2 justify-end">
            <Button
              variant="outline"
              onClick={reset}
              data-testid="filters-reset"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Azzera filtri
            </Button>
            <Button
              onClick={apply}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="filters-apply"
            >
              Applica filtri
            </Button>
          </div>
        </div>
      </div>

      <div className="panel" data-testid="movements-panel">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Data/Ora</th>
                <th>Codice</th>
                <th>Prodotto</th>
                <th>Tipo</th>
                <th className="text-right">Quantità</th>
                <th className="text-right">Giacenza dopo</th>
                <th>Note</th>
                <th className="text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-slate-500">
                    Caricamento...
                  </td>
                </tr>
              ) : movements.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-slate-500">
                    Nessun movimento con questi filtri.
                  </td>
                </tr>
              ) : (
                movements.map((m) => (
                  <tr key={m.id} data-testid={`movement-row-${m.id}`}>
                    <td className="mono text-slate-600 text-xs">
                      {new Date(m.timestamp).toLocaleString("it-IT")}
                    </td>
                    <td className="mono text-slate-600">{m.product_code}</td>
                    <td className="font-medium text-slate-900">
                      {m.product_name}
                    </td>
                    <td>
                      {m.type === "entrata" ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-xs uppercase tracking-wider">
                          <ArrowDownToLine className="w-3.5 h-3.5" />
                          Entrata
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-700 font-semibold text-xs uppercase tracking-wider">
                          <ArrowUpFromLine className="w-3.5 h-3.5" />
                          Uscita
                        </span>
                      )}
                    </td>
                    <td
                      className={`text-right mono font-bold ${
                        m.type === "entrata" ? "text-emerald-700" : "text-red-700"
                      }`}
                    >
                      {m.type === "entrata" ? "+" : "−"}
                      {m.quantity}
                    </td>
                    <td className="text-right mono">{m.balance_after}</td>
                    <td className="text-slate-600 text-sm max-w-xs truncate">
                      {m.note || "—"}
                    </td>
                    <td className="text-right">
                      <button
                        title="Annulla movimento"
                        onClick={() => setDeleteTarget(m)}
                        className="p-2 rounded hover:bg-red-50 text-red-600"
                        data-testid={`movement-delete-${m.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <MovementForm
        open={openForm}
        onOpenChange={async (o) => {
          setOpenForm(o);
          if (!o) await load();
        }}
      />

      <AlertDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="delete-movement-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Annullare questo movimento?</AlertDialogTitle>
            <AlertDialogDescription>
              L'annullamento ripristinerà la giacenza del prodotto. L'azione
              non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-movement-cancel">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-movement-confirm"
            >
              Annulla movimento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
